import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import {
  DataTable,
  DisclosureLink,
  InfoHint,
  Sparkline,
  type DataTableColumn,
  type DataTableGroup,
} from '../../../ui-kit';
import { formatValue, toSparklinePoints, trailingDelta } from '../format';
import type { PeriodDef, SeriesPoint, SpecTableRow, SpecTableSection } from '../../../types/manualDashboard';
import AgentBreakdown from './AgentBreakdown';

// Column widths, px. The sticky metric column needs a fixed width so the
// scroll shadow sits on its edge; the period columns only need a floor.
const METRIC_WIDTH = 300;
const TREND_WIDTH = 136;
const PERIOD_MIN_WIDTH = 84;
const SPARKLINE_WIDTH = 104;
const SPARKLINE_HEIGHT = 20;
/** Fewer cohorts than this make no line worth drawing; the trend column shows the change instead. */
const SPARKLINE_MIN_POINTS = 3;

type Row = SpecTableRow & { id: string };
type Tone = 'neutral' | 'warning' | 'danger';

interface SpecTableBlockProps {
  sections: SpecTableSection[];
  periods: PeriodDef[];
  showSparkline: boolean;
  /** Names the table for screen readers. */
  title?: string;
  /** Folded sections, by index as a string; kept by the caller so they survive a reload. */
  collapsedGroups?: string[];
  onCollapsedGroupsChange?: (keys: string[]) => void;
  /** Fill the height it's given, header pinned — the expanded view. */
  fill?: boolean;
  /**
   * Figures that open a per-agent list under their row: the metrics that have
   * one, and what the list needs to fetch it.
   */
  agentBreakdown?: {
    dashboardId: string;
    metrics: string[];
    filters: Record<string, string[]>;
  };
}

const isDerived = (row: SpecTableRow) => row.status === 'proxy';

/** How loudly a note should announce itself: derived figures loudest. */
const toneFor = (status: string | null | undefined, hasCaveat: boolean): Tone =>
  !hasCaveat ? 'neutral' : status === 'proxy' ? 'danger' : 'warning';

/**
 * the customer's sheet, row for row: a `DataTable` with this board's columns.
 *
 * What a row means, and anything that qualifies it, sits behind the same info
 * icon the metric cards use. The icon is amber or red when there is a caveat,
 * so a figure that needs care is marked before anyone hovers.
 */
export default function SpecTableBlock({
  sections, periods, showSparkline, title, collapsedGroups, onCollapsedGroupsChange, fill = false, agentBreakdown,
}: SpecTableBlockProps) {
  // One breakdown open at a time: the row it sits under and the cohort it is for.
  const [open, setOpen] = React.useState<{ rowId: string; period: string } | null>(null);
  const withBreakdown = React.useMemo(() => new Set(agentBreakdown?.metrics ?? []), [agentBreakdown?.metrics]);
  // A regroup or a new range can drop the open cohort; close rather than point at nothing.
  React.useEffect(() => {
    if (open && !periods.some((p) => p.key === open.period)) setOpen(null);
  }, [open, periods]);

  // Newest cohort first: it is the one people come to read, and it lands in
  // view without scrolling. The trend sparkline still runs oldest to newest,
  // left to right, as a line over time should.
  const newestFirst = React.useMemo(() => [...periods].reverse(), [periods]);

  const groups: DataTableGroup<Row>[] = React.useMemo(
    () =>
      sections.map((section, si) => {
        const notes = [section.note, section.info].filter(Boolean) as string[];
        return {
          key: String(si),
          title: section.title ?? '',
          info: notes.length ? <Notes items={notes} /> : undefined,
          infoTone: toneFor(section.status, notes.length > 0 && section.status !== 'ok'),
          rows: section.rows.map((row, ri) => ({ ...row, id: `${si}-${ri}` })),
        };
      }),
    [sections],
  );

  const drawLine = showSparkline && periods.length >= SPARKLINE_MIN_POINTS;

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'metric',
      header: 'Metric',
      width: METRIC_WIDTH,
      sticky: true,
      render: (row) => <MetricLabel row={row} />,
    },
    ...(showSparkline
      ? [{
          key: 'trend',
          header: 'Trend',
          width: TREND_WIDTH,
          render: (row: Row) => <TrendCell row={row} drawLine={drawLine} />,
        }]
      : []),
    ...newestFirst.map((period) => ({
      key: period.key,
      header: period.label,
      numeric: true,
      minWidth: PERIOD_MIN_WIDTH,
      render: (row: Row) => {
        const point = row.values?.find((v) => v.period === period.key);
        const breakable = Boolean(row.metric && withBreakdown.has(row.metric) && point?.value !== null && point);
        const isOpen = open?.rowId === row.id && open.period === period.key;
        return (
          <Stack alignItems="flex-end" spacing={0.25}>
            <Box component="span"><PeriodCell point={point} row={row} /></Box>
            {breakable && (
              <DisclosureLink
                open={isOpen}
                onClick={() => setOpen(isOpen ? null : { rowId: row.id, period: period.key })}
                aria-label={`Agent breakdown of ${row.label} for ${period.label}`}
              >
                Agent breakdown
              </DisclosureLink>
            )}
          </Stack>
        );
      },
    })),
  ];

  return (
    <DataTable
      ariaLabel={title ?? 'Metrics by cohort'}
      columns={columns}
      groups={groups}
      getRowKey={(row) => row.id}
      collapsedGroups={collapsedGroups}
      onCollapsedGroupsChange={onCollapsedGroupsChange}
      fill={fill}
      rowTone={(row) => (isDerived(row) ? 'danger' : null)}
      renderRowDetail={(row) =>
        agentBreakdown && open?.rowId === row.id && row.metric ? (
          <AgentBreakdown
            dashboardId={agentBreakdown.dashboardId}
            metricKey={row.metric}
            periods={periods}
            periodKey={open.period}
            onPeriodChange={(period) => setOpen({ rowId: row.id, period })}
            onClose={() => setOpen(null)}
            filters={agentBreakdown.filters}
          />
        ) : null
      }
      // A row the warehouse cannot answer has a reason, not a column of dashes.
      renderRowSpan={(row) =>
        row.values || !newestFirst.length
          ? null
          : {
              from: newestFirst[0].key,
              content: <Box component="span" sx={{ fontStyle: 'italic' }}>{row.note}</Box>,
            }
      }
    />
  );
}

/** The footer note for a table with derived rows, or null when it has none. */
export function specTableFooter(sections: SpecTableSection[]): React.ReactNode | null {
  if (!sections.some((s) => s.rows.some(isDerived))) return null;
  return (
    <>
      <Box component="span" sx={(t) => ({ mr: 0.5, fontWeight: 700, color: t.tokens.semantic.danger })}>
        &dagger;
      </Box>
      <strong>Derived, not measured.</strong> The warehouse has no direct field for these, so they are
      inferred from related records. Hover a row&rsquo;s info icon to see exactly how, and what that can get wrong.
    </>
  );
}

function MetricLabel({ row }: { row: Row }) {
  const derived = isDerived(row);
  const definition = row.definition ?? row.note;
  const hasInfo = Boolean(definition || row.caveat);

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, whiteSpace: 'normal' }}>
      <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0 }}>
        {row.label}
      </Typography>
      {derived && (
        <Tooltip title="Derived: the warehouse has no direct field for this">
          <Typography
            component="span"
            variant="caption"
            sx={(t) => ({ alignSelf: 'flex-start', fontWeight: 700, cursor: 'help', color: t.tokens.semantic.danger })}
          >
            &dagger;
          </Typography>
        </Tooltip>
      )}
      {hasInfo && (
        <InfoHint
          label={`About ${row.label}`}
          tone={toneFor(row.status, Boolean(row.caveat))}
          title={<Notes items={definition ? [definition] : []} caveat={row.caveat} />}
        />
      )}
    </Stack>
  );
}

/** What an info icon shows: the explanation, then any caveat marked as one. */
function Notes({ items, caveat }: { items: string[]; caveat?: string | null }) {
  return (
    <Stack spacing={0.75} sx={{ py: 0.25 }}>
      {items.map((text) => (
        <Typography key={text} variant="caption" sx={{ display: 'block' }}>
          {text}
        </Typography>
      ))}
      {caveat && (
        <Stack direction="row" spacing={0.5} alignItems="flex-start">
          <WarningAmberRounded sx={(t) => ({ mt: 0.25, fontSize: t.typography.caption.fontSize })} />
          <Typography variant="caption">{caveat}</Typography>
        </Stack>
      )}
    </Stack>
  );
}

function TrendCell({ row, drawLine }: { row: Row; drawLine: boolean }) {
  if (!row.values) return null;
  if (drawLine) {
    return (
      <Sparkline
        points={toSparklinePoints(row.values)}
        width={SPARKLINE_WIDTH}
        height={SPARKLINE_HEIGHT}
        formatValue={(v) => formatValue(v, row.format)}
        label={row.label}
      />
    );
  }
  // Too few cohorts to draw a line; the change on its own says the same.
  const delta = trailingDelta(row.values);
  if (delta === null) {
    return <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>—</Typography>;
  }
  return (
    <Typography
      variant="caption"
      sx={(t) => ({ fontWeight: 600, color: delta >= 0 ? t.tokens.semantic.success : t.tokens.semantic.danger })}
    >
      {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(0)}%
    </Typography>
  );
}

function PeriodCell({ point, row }: { point?: SeriesPoint; row: Row }) {
  if (!point) return null;
  const text = formatValue(point.value, row.format);
  if (!point.provisional || point.value === null) return <>{text}</>;
  return (
    <Tooltip title="This cohort has not had time to settle for this metric">
      <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>
        {text}
      </Box>
    </Tooltip>
  );
}
